import React, { useState, useEffect } from "react";
import { CoApplicantForm, CoApplicantFormData } from "./CoApplicantForm";
import { CoApplicantCard } from "./CoApplicantCard";
import { Button } from "@components/shadcn-ui/button";
import { useToast } from "@customHooks/useToast";
import { Plus, Loader2, Trash } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@components/shadcn-ui/dialog";

const isValidCoApplicant = (c?: CoApplicantFormData | null) => {
  if (!c) return false;
  const firstName = c.personalDetails?.firstName?.trim() || "";
  const lastName = c.personalDetails?.lastName?.trim() || "";
  const hasName = firstName !== "" || lastName !== "";
  const hasKycValue =
    c.kycDetails && Object.values(c.kycDetails).some((v) => typeof v === "string" ? v.trim() !== "" : !!v);
  const hasAddress = Array.isArray(c.addressId) && c.addressId.some((id) => typeof id === "string" && id.trim() !== "");
  const hasRelation = typeof c.relationToApplicant === "string" && c.relationToApplicant.trim() !== "";
  return hasName || hasKycValue || hasAddress || hasRelation;
};

interface CoApplicantManagerProps {
  applicationId: string;
  leadId: string;
  leadPhoneNumber: string;
  applicantPanNumber: string;
  initialCoApplicants?: CoApplicantFormData[] | null;
  onCoApplicantsUpdated?: (coApplicants: CoApplicantFormData[]) => void;
  readOnly?: boolean;
  existingReferencePhoneNumbers: string[];
}

export const CoApplicantManager: React.FC<CoApplicantManagerProps> = ({
  applicationId,
  leadId,
  leadPhoneNumber,
  applicantPanNumber,
  initialCoApplicants,
  onCoApplicantsUpdated,
  readOnly = false,
  existingReferencePhoneNumbers,
}) => {
  const [coApplicants, setCoApplicants] = useState<CoApplicantFormData[]>(
    initialCoApplicants && Array.isArray(initialCoApplicants) ? initialCoApplicants.filter(isValidCoApplicant) : []
  );
  const [isLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const { toast } = useToast();

  // Update co-applicant when initialCoApplicants changes (e.g., when loaded from API)
  useEffect(() => {
    if (initialCoApplicants && Array.isArray(initialCoApplicants)) {
      setCoApplicants(initialCoApplicants.filter(isValidCoApplicant));
    } else {
      setCoApplicants([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCoApplicants]);

  // Effect to notify parent component when co-applicant changes
  useEffect(() => {
    if (onCoApplicantsUpdated) {
      const valid = coApplicants.filter(isValidCoApplicant);
      onCoApplicantsUpdated(valid);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coApplicants, onCoApplicantsUpdated]);

  const handleCoApplicantAdded = (newCoApplicant: CoApplicantFormData) => {
    if (!isValidCoApplicant(newCoApplicant)) {
      toast({ description: "Co-applicant is empty and was not added." });
      setShowAddForm(false);
      return;
    }
    setCoApplicants((prev) => [...prev, newCoApplicant]);
    setShowAddForm(false);
    toast({
      description: "Co-applicant added successfully",
    });
  };

  const handleDeleteCoApplicant = (index: number) => {
    if (!window.confirm("Are you sure you want to remove this co-applicant?")) return;
    setCoApplicants((prev) => prev.filter((_, i) => i !== index));
    toast({ description: "Co-applicant removed successfully" });
  };

  const handleEditCoApplicant = () => {
    setIsEditDialogOpen(true);
  };

  const handleUpdateCoApplicant = (updatedCoApplicant: CoApplicantFormData) => {
    if (editingIndex === null) return;
    if (!isValidCoApplicant(updatedCoApplicant)) {
      // if updated to an empty object, remove it
      setCoApplicants((prev) => prev.filter((_, i) => i !== editingIndex));
      toast({ description: "Empty co-applicant removed." });
    } else {
      setCoApplicants((prev) => prev.map((c, i) => (i === editingIndex ? updatedCoApplicant : c)));
      toast({ description: "Co-applicant updated successfully" });
    }
    setEditingIndex(null);
    setIsEditDialogOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading co-applicant details...</span>
      </div>
    );
  }
  // Debug log to check state
  console.log("CoApplicantManager state:", {
    coApplicants,
    initialCoApplicants,
    showAddForm,
    isEditDialogOpen,
    editingIndex,
  });
  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-4 mt-4">
        <h2 className="text-lg font-semibold">Co-Applicant Details</h2>
        {!readOnly && (
          <Button 
            onClick={() => setShowAddForm(true)}
            size="sm"
            className="flex items-center gap-1"
          >
            <Plus className="h-4 w-4" /> Add Co-Applicant
          </Button>
        )}
      </div>

      {showAddForm && (
        <CoApplicantForm
          applicationId={applicationId}
          leadId={leadId}
          onCoApplicantAdded={handleCoApplicantAdded}
          onCancel={() => setShowAddForm(false)}
          existingPhoneNumbers={existingReferencePhoneNumbers}
          leadPhoneNumber={leadPhoneNumber}
          applicantPanNumber={applicantPanNumber}
        />
      )}

      {coApplicants.length === 0 && !showAddForm && (
        <div className="text-center p-8 border border-dashed rounded-md">
          <p className="text-gray-500">No co-applicant added yet</p>
          {!readOnly && (
            <Button 
              onClick={() => setShowAddForm(true)}
              variant="outline"
              className="mt-2"
            >
              Add co-applicant
            </Button>
          )}
        </div>
      )}

      {coApplicants.length > 0 && (
        <div className="grid gap-4">
          {coApplicants.map((c, i) => (
            <div key={i} className="relative">
              <CoApplicantCard
                coApplicant={c}
                onEdit={() => {
                  setEditingIndex(i);
                  setIsEditDialogOpen(true);
                }}
                applicationId={applicationId}
              />
              {!readOnly && (
                <button
                  onClick={() => handleDeleteCoApplicant(i)}
                  aria-label={`Remove co-applicant ${i + 1}`}
                  className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                  title="Remove co-applicant"
                >
                  <Trash className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit Co-Applicant Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-auto">
          <DialogHeader className="sticky top-0 bg-white z-10 pb-2">
            <DialogTitle>Edit Co-Applicant</DialogTitle>
          </DialogHeader>
          {editingIndex !== null && coApplicants[editingIndex] && (
            <CoApplicantForm
              applicationId={applicationId}
              leadId={leadId}
              onCoApplicantAdded={handleUpdateCoApplicant}
              onCancel={() => setIsEditDialogOpen(false)}
              initialData={coApplicants[editingIndex]}
              isEditMode={true}
              existingPhoneNumbers={existingReferencePhoneNumbers}
              leadPhoneNumber={leadPhoneNumber}
              applicantPanNumber={applicantPanNumber}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
